<template>
    <SeoHead :seo="seo" />
    <MainLayout>
        <PageHeader title="Судьи">
            <p class="page-subtitle">
                <template v-if="isFiltered">
                    Отображается {{ filteredMembers.length }} из {{ members.length }} судей
                </template>
                <template v-else>{{ members.length }} судей</template>
            </p>
        </PageHeader>

        <div class="member-directory-filters">
            <div class="member-directory-filters__row">
                <UiInput
                    v-model="searchQuery"
                    type="search"
                    label="Поиск"
                    placeholder="Имя или регион..."
                    :block="false"
                />

                <UiSelect
                    v-model="selectedCategory"
                    label="Категория"
                    :options="categoryOptions"
                    :block="false"
                    align="left"
                    value-type="string"
                />

                <UiSelect
                    v-model="selectedRegion"
                    label="Регион"
                    :options="regionOptions"
                    :block="false"
                    align="left"
                    value-type="string"
                />
            </div>
        </div>

        <div v-if="displayMembers.length" class="member-poster-grid">
            <MemberPosterCard
                v-for="member in displayMembers"
                :key="member.id"
                :full-name="member.full_name"
                :surname="member.surname"
                :first-name="member.first_name"
                :middle-name="member.middle_name"
                :middle-name-visible="member.middle_name_visible"
                :photo-url="member.photo_url"
                :show-season-meta="true"
                :position="member.position"
            />
        </div>

        <div v-else class="member-directory-empty">
            <p>Судьи не найдены</p>
        </div>
    </MainLayout>
</template>

<script setup>
import { computed, ref } from 'vue';
import SeoHead from '@/Components/SeoHead.vue';
import MainLayout from '@/components/layout/MainLayout.vue';
import PageHeader from '@/components/layout/PageHeader.vue';
import MemberPosterCard from '@/components/directory/MemberPosterCard.vue';
import UiInput from '@/components/ui/forms/UiInput.vue';
import UiSelect from '@/components/ui/forms/UiSelect.vue';

const props = defineProps({
    seo: {
        type: Object,
        default: null,
    },
    members: {
        type: Array,
        default: () => [],
    },
});

const searchQuery = ref('');
const selectedCategory = ref('all');
const selectedRegion = ref('all');

const categoryOptions = computed(() => {
    const seen = new Map();
    (props.members || []).forEach((member) => {
        if (!member.category) return;
        if (!seen.has(member.category)) {
            seen.set(member.category, member.category_name || member.category);
        }
    });
    return [
        { value: 'all', label: 'Все' },
        ...[...seen.entries()]
            .map(([value, label]) => ({ value, label }))
            .sort((a, b) => a.label.localeCompare(b.label, 'ru')),
    ];
});

const regionOptions = computed(() => {
    const regions = new Set();
    (props.members || []).forEach((member) => {
        if (member.region) {
            regions.add(member.region);
        }
    });
    return [
        { value: 'all', label: 'Все' },
        ...[...regions].sort((a, b) => a.localeCompare(b, 'ru')).map((region) => ({
            value: region,
            label: region,
        })),
    ];
});

const isFiltered = computed(() =>
    searchQuery.value !== ''
    || selectedCategory.value !== 'all'
    || selectedRegion.value !== 'all',
);

const filteredMembers = computed(() => {
    let result = props.members || [];

    if (selectedCategory.value !== 'all') {
        result = result.filter((member) => member.category === selectedCategory.value);
    }

    if (selectedRegion.value !== 'all') {
        result = result.filter((member) => member.region === selectedRegion.value);
    }

    if (searchQuery.value) {
        const query = searchQuery.value.toLowerCase();
        result = result.filter((member) =>
            member.full_name?.toLowerCase().includes(query)
            || member.region?.toLowerCase().includes(query),
        );
    }

    return result;
});

const displayMembers = computed(() =>
    filteredMembers.value.map((member) => ({
        id: member.id,
        full_name: member.full_name,
        surname: member.surname,
        first_name: member.first_name,
        middle_name: member.middle_name,
        middle_name_visible: member.middle_name_visible,
        photo_url: member.photo_url,
        position: member.category_name || member.category || null,
    })),
);
</script>
